<script setup lang="ts">
import { computed, ref } from 'vue'

/**
 * The only `<button>` in the application — raw `<button>` is lint-banned outside this
 * folder, so every control in the panel inherits the same focus ring, the same height and
 * the same loading behaviour.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'high-risk'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    type?: 'button' | 'submit'
    loading?: boolean
    disabled?: boolean
    /**
     * The control exists but cannot act **yet** — a capability that is not built, or a
     * precondition the operator can fix.
     *
     * Rendered as `aria-disabled` with the click suppressed, **never** as the native
     * `disabled` attribute. A natively disabled control leaves the tab order and takes its
     * tooltip with it, so the reason it cannot be used becomes unreachable by keyboard and
     * by screen reader — which is the one audience that most needs telling.
     */
    unavailable?: boolean
    block?: boolean
  }>(),
  { variant: 'primary', type: 'button', loading: false, disabled: false, unavailable: false, block: false },
)

const emit = defineEmits<{ click: [MouseEvent] }>()

const root = ref<HTMLButtonElement | null>(null)

/**
 * Exposed so a parent can move focus here without reaching into `$el`, which is typed
 * `any` and so is a place a typo survives. `BaseDialog` focuses Cancel on open; nothing
 * else needs this, and nothing else should: focus management is a dialog's job.
 */
defineExpose({
  focus: (): void => {
    root.value?.focus()
  },
})

/** `disabled` is for "not applicable"; `unavailable` keeps focus. See the prop comment. */
const inert = computed(() => props.disabled || props.loading)

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-action text-action-ink border-action hover:bg-action-hover active:bg-action-active',
  secondary: 'bg-raised text-ink border-border-default hover:border-ink-muted',
  ghost: 'bg-transparent text-ink-secondary border-transparent hover:bg-sunken',
  danger: 'bg-danger text-action-ink border-danger hover:opacity-90',
  // Visually distinct from `danger`: destructive means "this row disappears", high-risk
  // means "this reaches a live merchant storefront". Conflating them trains an operator to
  // read the same colour two ways.
  'high-risk': 'bg-warn text-action-ink border-warn hover:opacity-90',
}

function onClick(event: MouseEvent): void {
  if (props.unavailable || inert.value) {
    event.preventDefault()

    return
  }

  emit('click', event)
}
</script>

<template>
  <button
    ref="root"
    :type="type"
    :disabled="inert"
    :aria-disabled="unavailable || undefined"
    :aria-busy="loading || undefined"
    :class="[
      'inline-flex h-9 items-center justify-center gap-2 rounded-control border px-4',
      'text-body font-medium transition-[color,background-color,border-color,transform] duration-(--motion-fast)',
      // 2% on press, and no more. It is the whole of the tactile feedback a button here
      // gets: enough that a click feels received on a slow connection, small enough that
      // a toolbar of six does not look springy. Nothing moves on a disabled control,
      // which would read as the click having worked.
      'active:scale-[0.98] disabled:active:scale-100',
      'disabled:opacity-60',
      VARIANTS[variant],
      block ? 'w-full' : '',
      unavailable ? 'opacity-60' : '',
    ]"
    @click="onClick"
  >
    <!--
      The label stays put and a marker is APPENDED.
      Replacing it with a spinner changes the button's width mid-click, which moves
      whatever sits beside it — and on a form that is usually the cancel button.
    -->
    <slot />
    <span
      v-if="loading"
      aria-hidden="true"
      >· · ·</span
    >
  </button>
</template>
